import React, { useMemo } from "react";
import ReactSelect from "react-select";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import SectionHeader from "@dhn/components/molecules/section-header";
import ReactTable from "@dhn/components/molecules/data-table";
import { formatFileSize } from "@dhn/shared/utils/file-size";
import StatusBadge from "@dhn/components/molecules/status-badge";

const ACCEPTED_EVIDENCE_TYPES =
  ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

export default function SpecialityEvidenceTable({
  specialities = [],
  specialityDocuments = {},
  specialityOptions = [],
  selectedSpeciality = null,
  onSelectedSpecialityChange,
  onAddSpeciality,
  onRemoveSpeciality,
  onSpecialityFileChange,
}) {
  const availableSpecialityOptions = useMemo(
    () =>
      specialityOptions.filter(
        (option) =>
          !specialities.some(
            (speciality) => Number(speciality.id) === Number(option.id),
          ),
      ),
    [specialities, specialityOptions],
  );
  const completedEvidenceCount = specialities.filter(
    (speciality) => specialityDocuments[speciality.id],
  ).length;
  const missingEvidenceCount = specialities.length - completedEvidenceCount;

  const columns = useMemo(
    () => [
      {
        Header: "Especialidad",
        accessor: "name",
      },
      {
        Header: "Evidencia",
        Cell: ({ original }) => {
          const document = specialityDocuments[original.id];

          if (!document) {
            return (
              <span className="text-muted">
                <i className="fa fa-paperclip mr-1" />
                Sin evidencia adjunta
              </span>
            );
          }

          return (
            <div style={{ minWidth: 0 }}>
              <div className="font-weight-bold text-truncate">
                <i className="fa fa-file-text-o text-info mr-2" />
                {document.name}
              </div>
              <small className="text-muted">
                {formatFileSize(document.size)}
              </small>
            </div>
          );
        },
      },
      {
        Header: "Estado",
        width: 120,
        Cell: ({ original }) =>
          specialityDocuments[original.id] ? (
            <Badge color="success" pill>
              <i className="fa fa-check mr-1" />
              Completa
            </Badge>
          ) : (
            <StatusBadge color="warning" icon="fa-exclamation-circle">
              Pendiente
            </StatusBadge>
          ),
      },
      {
        Header: "Acciones",
        width: 270,
        Cell: ({ original }) => {
          const document = specialityDocuments[original.id];
          const inputId = `associated-doctor-speciality-${original.id}`;

          return (
            <div className="d-flex align-items-center">
              <Label
                for={inputId}
                className="btn btn-primary btn-sm mb-0 mr-2"
              >
                <i className="fa fa-upload mr-1" />
                {document ? "Reemplazar" : "Adjuntar"}
              </Label>
              <Input
                id={inputId}
                type="file"
                accept={ACCEPTED_EVIDENCE_TYPES}
                className="d-none"
                onChange={(event) =>
                  onSpecialityFileChange(original.id, event)
                }
              />
              <Button
                type="button"
                color="danger"
                size="sm"
                className="btn-color-danger ml-2"
                title="Borrar especialidad"
                onClick={() => onRemoveSpeciality(original.id)}
              >
                <i className="fa fa-times" aria-hidden="true" />
                {" Borrar"}
              </Button>
            </div>
          );
        },
      },
    ],
    [
      onRemoveSpeciality,
      onSpecialityFileChange,
      specialityDocuments,
    ],
  );

  return (
    <Card className="mb-0">
      <CardBody>
        <SectionHeader
          compact
          icon="fa-graduation-cap"
          title="Especialidades y evidencias"
          description="Cada especialidad será revisada y aprobada individualmente."
          actions={
            <StatusBadge
              color={missingEvidenceCount ? "warning" : "success"}
            >
              {completedEvidenceCount} de {specialities.length} completas
            </StatusBadge>
          }
        />

        <FormGroup>
          <Label className="font-weight-bold">Especialidades solicitadas</Label>
          <Row form>
            <Col md={3} className="mb-2 mb-md-0">
              <ReactSelect
                placeholder="Seleccione una especialidad"
                options={availableSpecialityOptions}
                value={selectedSpeciality}
                getOptionLabel={(item) => item.name}
                getOptionValue={(item) => item.id}
                onChange={onSelectedSpecialityChange}
                isClearable
              />
            </Col>
            <Col md="auto" className="d-flex align-items-end">
              <Button
                color="primary"
                type="button"
                className="btn-color-primary"
                disabled={!selectedSpeciality}
                onClick={onAddSpeciality}
              >
                <i className="fa fa-plus" aria-hidden="true" />
                {" Agregar"}
              </Button>
            </Col>
          </Row>
        </FormGroup>

        <div className="mt-3">
          <ReactTable
            className="-striped -highlight"
            data={specialities}
            columns={columns}
            pageSize={specialities.length || 1}
            minRows={1}
            showPagination={false}
            noDataText="Agregue al menos una especialidad para adjuntar su evidencia"
          />
        </div>

        {missingEvidenceCount > 0 && (
          <div className="alert alert-warning d-flex align-items-center mb-0 mt-3 py-2">
            <i className="fa fa-exclamation-triangle mr-2" />
            Debe adjuntar una evidencia para cada especialidad antes de procesar
            la solicitud.
          </div>
        )}
        <small className="text-muted d-block mt-2">
          Formatos permitidos: PDF, JPG o PNG. Un archivo por especialidad.
        </small>
      </CardBody>
    </Card>
  );
}
